/**
 * This file contains the GraphQL operations for exchanging a Google auth code
 * for an ID token (JWT token).
 */
import gql from "graphql-tag";

import type {ApolloGqlClient} from "@repo/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";
import {Errors, KAError} from "@repo/logging";

import type {exchangeGoogleCodeForIDTokenMutationType} from "./__graphql-types__/index.ts";

export const EXCHANGE_GOOGLE_CODE_FOR_IDTOKEN_MUTATION: GqlOperation<
    exchangeGoogleCodeForIDTokenMutationType["response"],
    exchangeGoogleCodeForIDTokenMutationType["variables"]
> = gqlOp(gql`
    mutation exchangeGoogleCodeForIDTokenMutation(
        $code: String!
        $redirectURI: String!
    ) {
        exchangeGoogleCodeForIDToken(code: $code, redirectURI: $redirectURI) {
            idToken
            error {
                code
            }
        }
    }
`);

/**
 * Exchange a Google auth code for an ID token (JWT token) that can get the
 * user's Google profile.
 *
 * This exchanges a Google authorization code from Google's authentication flow
 * for an ID token that can get the user's Google profile.
 *
 * @param {ApolloGqlClient} gqlClient The GraphQL client.
 * @param {string} code The Google auth code.
 * @param {string} redirectURI The redirect URI.
 *
 * @returns {Promise<string>} The ID token.
 */
export const exchangeGoogleCodeForIDToken = async (
    gqlClient: ApolloGqlClient,
    code: string,
    redirectURI: string,
): Promise<string> => {
    try {
        const result = await gqlClient.mutate(
            EXCHANGE_GOOGLE_CODE_FOR_IDTOKEN_MUTATION,
            {
                variables: {
                    code,
                    redirectURI,
                },
            },
        );

        const error = result.data?.exchangeGoogleCodeForIDToken?.error;
        if (error) {
            const {code} = error;

            // We pass the error code directly, so that code is mapped to an
            // UI readable message.
            throw new KAError(code, Errors.Internal);
        }

        return result.data?.exchangeGoogleCodeForIDToken?.idToken ?? "";
    } catch (error: any) {
        // We handle the exception thrown from within the mutation success
        // handler.
        if (error instanceof KAError) {
            throw error;
        }

        // Otherwise we throw a generic error.
        throw new KAError("Failed to exchange code", Errors.Internal, {
            cause: error,
        });
    }
};
